/**
 * WCAG 2.2 contrast maths.
 *
 * This lives in `backend/src/lib` on purpose. The Accessibility Advocate persona
 * holds a merge veto on contrast and states the bar as "checked against the token
 * palette rather than per-screen" — putting the maths here puts it under the 90%
 * coverage gate, so the token audit in `token-contrast.test.ts` cannot rot.
 *
 * Pure functions, no DOM: the tests run in the `node` environment.
 */

export type Rgb = { r: number; g: number; b: number };

const HEX_PATTERN = /^#?(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** WCAG AA floors. */
export const AA_NORMAL_TEXT = 4.5;
export const AA_LARGE_TEXT = 3;
/** Non-text UI components and graphical objects — WCAG 1.4.11. */
export const AA_NON_TEXT = 3;

/**
 * Parse a 3- or 6-digit hex colour into 0-255 channels.
 * Throws rather than returning a fallback: a silently-wrong colour would make the
 * contrast audit pass while shipping an unreadable token.
 */
export function parseHex(hex: string): Rgb {
  if (!HEX_PATTERN.test(hex)) {
    throw new Error(`Invalid hex colour: ${JSON.stringify(hex)}`);
  }

  const raw = hex.startsWith("#") ? hex.slice(1) : hex;
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

/** Undo the sRGB transfer function for a single 0-255 channel. */
function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance per WCAG 2.2 — 0 for black, 1 for white. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** Contrast ratio between two colours, 1:1 to 21:1. Order does not matter. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export type ContrastOptions = {
  /** Text at >=24px, or >=18.66px bold — relaxes the floor to 3:1. */
  large?: boolean;
  /** Non-text UI such as borders, focus rings and meter fills — 3:1. */
  nonText?: boolean;
  /** Overrides the preset floors entirely. */
  minimum?: number;
};

/** Required ratio for the given usage. An explicit `minimum` always wins. */
export function requiredRatio(options: ContrastOptions = {}): number {
  if (options.minimum !== undefined) return options.minimum;
  if (options.nonText) return AA_NON_TEXT;
  if (options.large) return AA_LARGE_TEXT;
  return AA_NORMAL_TEXT;
}

/** Whether a foreground/background pair clears the WCAG AA floor for its usage. */
export function meetsContrast(
  foreground: string,
  background: string,
  options: ContrastOptions = {},
): boolean {
  return contrastRatio(foreground, background) >= requiredRatio(options);
}
